import { useState } from "react";
import { newClientId } from "../idb";
import type { Snapshot, Enqueue } from "../types";
import { Card } from "../components/Card";

/** Downscale a captured image to ~1600px JPEG before enqueue — daily photos are bulk, and full-res
 *  would blow the IndexedDB outbox quota (and block check-ins/expenses). Falls back to the raw
 *  data URL when the canvas can't decode the source (e.g. iOS HEIC). */
function downscaleImage(file: File): Promise<string> {
  const MAX = 1600;
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === "string" ? reader.result : "";
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, MAX / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) return resolve(dataUrl);
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        } catch {
          resolve(dataUrl); // canvas can't draw it (HEIC) → send raw, server decodes
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

export function PhotosTab({ snap, enqueue }: { snap: Snapshot; enqueue: Enqueue }) {
  const [image, setImage] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setImage(await downscaleImage(file));
    setBusy(false);
  };

  const submit = async () => {
    if (!image) return;
    const clientId = newClientId();
    const photoDate = new Date().toLocaleDateString("en-CA"); // device-local YYYY-MM-DD
    const created = {
      id: clientId,
      thumbUrl: image,
      url: image,
      caption: caption.trim() || null,
      photoDate,
      createdAt: new Date().toISOString(),
    };
    await enqueue(
      clientId,
      "photo",
      "Trip photo",
      { image, caption: caption.trim() || null, photoDate, clientId },
      (s) => ({ ...s, dailyPhotos: [created, ...s.dailyPhotos], photoSharedToday: true }),
    );
    setImage(null);
    setCaption("");
  };

  return (
    <>
      <Card title="Share today's photo">
        {!snap.photoSharedToday && (
          <p className="mb-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
            No photo shared today — a quick snap from the road keeps the office (and travellers) in
            the loop.
          </p>
        )}
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-6 text-sm text-gray-600">
          {busy
            ? "Processing…"
            : image
              ? "📷 Photo ready — tap to replace"
              : "📷 Take or pick a photo"}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </label>
        {image && (
          <img src={image} alt="Preview" className="mt-2 max-h-48 w-full rounded-lg object-cover" />
        )}
        <input
          className="mt-2 w-full rounded-lg border border-gray-300 p-2 text-sm"
          placeholder="Caption (optional)"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
        />
        <button
          onClick={submit}
          disabled={!image || busy}
          className="bg-brand mt-2 w-full rounded-lg py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          Share photo
        </button>
      </Card>

      {snap.dailyPhotos.length > 0 && (
        <Card title={`Shared photos (${snap.dailyPhotos.length})`}>
          <div className="grid grid-cols-3 gap-2">
            {snap.dailyPhotos.map((p) => (
              <div key={p.id} className="overflow-hidden rounded-lg bg-gray-100">
                {p.thumbUrl ? (
                  <img
                    src={p.thumbUrl}
                    alt={p.caption || "Trip photo"}
                    className="aspect-square w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center text-[11px] text-gray-400">
                    Processing…
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </>
  );
}
